import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import {
  FeedbackListResponse,
  FeedbackWithUser,
  UserAppFeedbackRow,
} from "../../models/feedback";
import { logException } from "@punchup/sentry";

export async function fetchUserAppFeedback(
  supabaseClient: SupabaseClient<Database>,
  options?: { limit?: number; offset?: number }
): Promise<FeedbackListResponse> {
  const limit = options?.limit || 50;
  const offset = options?.offset || 0;

  try {
    // Query feedback ordered by newest first
    const {
      data: feedback,
      error,
      count,
    } = await supabaseClient
      .from("user_app_feedback")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      logException(error, {
        tags: {
          action: "fetchUserAppFeedback",
        },
      });
      throw error;
    }

    // Fetch user details for those with user_id
    const userIds =
      feedback?.filter((f) => f.user_id).map((f) => f.user_id) ?? [];
    const uniqueUserIds = [...new Set(userIds)] as string[];
    const userMap = new Map<string, { id: string; email: string | null }>();

    if (uniqueUserIds.length > 0) {
      // Use Supabase Admin API to get user emails in parallel
      await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            const { data: user, error: userError } =
              await supabaseClient.auth.admin.getUserById(userId);

            if (!userError && user.user) {
              userMap.set(userId, {
                id: user.user.id,
                email: user.user.email ?? null,
              });
            }
          } catch (err) {
            // If user not found, just skip (user may have been deleted)
            logException(err, {
              tags: {
                action: "fetchUserAppFeedback",
                user_id: userId,
              },
            });
          }
        })
      );
    }

    // Enrich feedback with user data
    const enrichedFeedback: FeedbackWithUser[] = (feedback ?? []).map((fb) => ({
      ...fb,
      user: fb.user_id ? userMap.get(fb.user_id) : undefined,
    }));

    return {
      feedback: enrichedFeedback,
      total: count ?? 0,
      hasMore: offset + limit < (count ?? 0),
    };
  } catch (error) {
    logException(error, {
      tags: {
        action: "fetchUserAppFeedback",
      },
    });
    throw error;
  }
}

export async function fetchUserAppFeedbackPage(
  supabaseClient: SupabaseClient<Database>,
  options: { afterId: number; limit: number; newestFirst: boolean }
): Promise<UserAppFeedbackRow[]> {
  const { data, error } = await supabaseClient
    .from("user_app_feedback")
    .select("*")
    .gt("id", options.afterId)
    .order("id", { ascending: !options.newestFirst })
    .limit(options.limit);

  if (error) {
    logException(error, { tags: { action: "fetchUserAppFeedbackPage" } });
    throw error;
  }

  return data ?? [];
}
